import {
  Alert,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Code } from 'src/components/code/Code';
// import { graphPersistedQueriesRouteConfig } from 'src/app/graph/routes';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { DocsRouter } from 'src/lib/routers';
// import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
// import { useRouteParams } from 'src/hooks/useRouteParams';

import { PersistedQueryListConfigHeader } from '../PersistedQueryListConfigHeader';
import {
  PersistedQueryListConfigStep,
  PersistedQueryListConfigWorkflow,
} from '../steps';

export interface PersistedQueryListConfigureModalState {
  graphId?: string;
  name?: string;
  listId?: string;
  linkedVariantId?: string;
  publishedOperationCount?: number;
}

type RouterConfigMode = 'pqs' | 'monitor' | 'safelist';
interface RouterConfigInstructions {
  title: string;
  description: string;
  yaml: string;
  requiresWarning: boolean;
}

const configureRouterInstructions = new Map<
  RouterConfigMode,
  RouterConfigInstructions
>([
  [
    'pqs',
    {
      title: 'Allow persisted query IDs',
      description:
        "Simply enabling persisted queries will allow the operations in the variant's linked PQ List to be queried by their associated persisted query IDs:",
      yaml: `\
persisted_queries:
  enabled: true
`,
      requiresWarning: false,
    },
  ],
  [
    'monitor',
    {
      title: 'Monitor unpersisted operations',
      description:
        'Your router can allow all GraphQL operations, with addition logging for unregistered operations:',
      yaml: `\
persisted_queries:
  enabled: true
    safelist:
      enabled: false
      log_unknown: true
`,
      requiresWarning: false,
    },
  ],
  [
    'safelist',
    {
      title: 'Enforce safelisting',
      description:
        'Your router can allow only GraphQL operations that match a registered shape, while rejecting unregistered operations:',
      yaml: `\
persisted_queries:
  enabled: true
  safelist: true
apq:
  enabled: false
`,
      requiresWarning: true,
    },
  ],
]);

interface Props {
  isOpen: boolean;
  state: PersistedQueryListConfigureModalState | null;
  onComplete: () => void;
  onPrevious: () => void;
}

export const PersistedQueryListConfigureModal = ({
  isOpen,
  // state,
  onComplete,
  onPrevious,
}: Props) => {
  // const { graphId: graphIdFromUrl } = useRouteParams(
  //   graphPersistedQueriesRouteConfig,
  // );
  const [activeTab, setActiveTab] = useState<RouterConfigMode>('pqs');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const instructions = configureRouterInstructions.get(activeTab)!;
  return (
    <Modal isOpen={isOpen} size="3xl" onClose={onComplete}>
      <ModalOverlay />
      <FormWrapper onSubmit={onComplete} noValidate>
        <ModalContent>
          <ModalHeader>
            <PersistedQueryListConfigHeader
              currentStep={PersistedQueryListConfigStep.Configure}
              workflow={PersistedQueryListConfigWorkflow.create}
            />
            <ModalTitle>Next Steps: Configure Your Router</ModalTitle>
            <ModalDescription>
              When you are ready to start testing, you'll need to update your
              router configuration. Below are some examples for how to use
              persisted queries.{' '}
              <ClickableText
                className="text-link hover:underline"
                as="a"
                rel="noopener noreferrer"
                target="_blank"
                href={DocsRouter.path('PersistedQueries')}
              >
                See our docs
              </ClickableText>{' '}
              for more details and advanced configurations.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <section>
              <div>
                <HeaderTab
                  tabName={configureRouterInstructions.get('pqs')?.title}
                  tabID="pqs"
                  isActive={() => activeTab === 'pqs'}
                  onClick={() => {
                    setActiveTab('pqs');
                  }}
                />
                <HeaderTab
                  tabName={configureRouterInstructions.get('monitor')?.title}
                  tabID="monitor"
                  isActive={() => activeTab === 'monitor'}
                  onClick={() => {
                    setActiveTab('monitor');
                  }}
                />
                <HeaderTab
                  tabName={configureRouterInstructions.get('safelist')?.title}
                  tabID="safelist"
                  isActive={() => activeTab === 'safelist'}
                  onClick={() => {
                    setActiveTab('safelist');
                  }}
                />
              </div>
              <section className="rounded border border-primary bg-silver-lighter p-4">
                <p className="text-neutral">{instructions.description}</p>
                <Code
                  className="mt-4"
                  code={instructions.yaml ?? ''}
                  showCopyButton
                />
                {instructions.requiresWarning && (
                  <Alert status="warning" className="mt-4">
                    Safelisting is designed to block traffic to your graph.
                    Please ensure that your safelist is configured properly
                    before turning this on for production traffic.
                  </Alert>
                )}
              </section>
            </section>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onPrevious}>
                Previous
              </Button>
              <Button variant="primary" type="submit">
                Finish
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
